import type { Updater } from '@tanstack/react-query';

import type { AnythingButFunction } from 'src/types/utils';

/**
 * redux-toolkit 에서 reducer 선언하듯이 react-query 에서 optimistic update 하기
 *
 * @param cb - data 를 바꾸는 로직
 * @template T - QueryData 타입
 * @returns react-query 의 QueryClient.setQueryData 에서 사용할 수 있는 updater function
 * @example
 * const updater = optimisticUpdaterFactory<User>((previous) => {
 *   previous.name = 'john';
 * });
 * queryClient.setQueryData('user', updater);
 */
export function optimisticUpdaterFactory<T = unknown>(
  cb: (deepCopiedPrev: AnythingButFunction<T>) => AnythingButFunction<T | Partial<T> | void>
) {
  const updater: Updater<AnythingButFunction<T | undefined>, AnythingButFunction<T | undefined>> = (
    previousData
  ) => {
    if (previousData == null) return undefined;

    const nextData = JSON.parse(JSON.stringify(previousData)) as AnythingButFunction<T>;

    if (nextData == null) return nextData;

    const result = cb(nextData);

    if (Array.isArray(nextData) && (result == null || Array.isArray(result))) {
      // Ensure the return type is AnythingButFunction<T | undefined>
      // If T is an array, nextData is already AnythingButFunction<T>
      // But to satisfy the type, we must cast
      return [...nextData, ...((result as unknown[]) ?? [])] as unknown as AnythingButFunction<
        T | undefined
      >;
    }

    return { ...nextData, ...result };
  };

  return updater;
}
